/**
 * 本机说话音量采样。
 *
 * 「谁在说话」这件事在 WebRTC 下拿不到全房间的音量：每个人只解自己订阅
 * 的那几路音频，而且解出来的是对方编码后的流，算音量既费 CPU 又不准。
 * 所以改成发言人自报、服务端转发（服务端只转持麦者的，见 room/mic.go）。
 *
 * 老系统是客户端轮询 GetUsrAudioLevelByID 主动去问每个人的音量，
 * 那在 50 人的教室里就是每秒几百次查询。
 */

/** 采样间隔。人耳能感知的说话起止在 100ms 级，再密就是白烧 CPU。 */
const SAMPLE_MS = 150

/** 时域平滑系数：0 表示不平滑。取 0.7 让指示灯不会一个字一个字地闪。 */
const SMOOTHING = 0.7

export interface SpeakMeter {
  stop(): void
}

/**
 * 开始采样并把音量交给 onLevel（0-100）。
 *
 * 返回的对象必须在换麦克风或离开房间时 stop()，否则 AudioContext
 * 会一直挂着 —— 浏览器对同时存在的 AudioContext 数量有上限，
 * 泄漏几次之后新的音频功能就会静默失效。
 */
export function startSpeakMeter(
  stream: MediaStream,
  onLevel: (level: number) => void,
): SpeakMeter | null {
  const track = stream.getAudioTracks()[0]
  if (!track) return null

  // Safari 到现在仍只认前缀名。
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null

  let ctx: AudioContext
  try {
    ctx = new Ctor()
  } catch {
    // 浏览器不给建（多半是 AudioContext 数量到顶），静默降级：
    // 没有音量指示总好过整个教室崩掉。
    return null
  }

  const source = ctx.createMediaStreamSource(new MediaStream([track]))
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  analyser.smoothingTimeConstant = SMOOTHING
  source.connect(analyser)

  const buf = new Uint8Array(analyser.frequencyBinCount)
  const timer = window.setInterval(() => {
    analyser.getByteFrequencyData(buf)
    // 用均方根而不是峰值：峰值会被一次键盘敲击顶满，
    // 而说话是持续的能量，均方根更贴近「这个人在讲话」。
    let sum = 0
    for (const v of buf) sum += v * v
    const rms = Math.sqrt(sum / buf.length)
    onLevel(Math.min(100, Math.round((rms / 255) * 100 * 2.5)))
  }, SAMPLE_MS)

  return {
    stop() {
      window.clearInterval(timer)
      source.disconnect()
      analyser.disconnect()
      void ctx.close()
    },
  }
}
